import { Injectable } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';

@Injectable({
  providedIn: 'root',
})
export class TrackingNumberValidatorUtil {
  oneZedPattern = /^1Z[0-9A-Z]{8}\d{8}$/;
  threeZedPattern = /^3Z[0-9A-Z]{8}\d{8}$/;
  elevenDigitPattern = /^[0-9AC-Z]\d{10}$/;
  elevenZDigitPattern = /^Z[0-9AC-Z]\d{9}$/;

  private convert(c: string): number {
    const numberRegex = /\d/;

    if (numberRegex.test(c)) {
      return Number.parseInt(c);
    }

    switch (c) {
      case 'I':
      case 'S':
        return 0;
      case 'J':
      case 'T':
        return 1;
      case 'A':
      case 'K':
      case 'U':
        return 2;
      case 'B':
      case 'V':
      case 'L':
        return 3;
      case 'C':
      case 'W':
      case 'M':
        return 4;
      case 'D':
      case 'N':
      case 'X':
        return 5;
      case 'E':
      case 'O':
      case 'Y':
        return 6;
      case 'F':
      case 'P':
      case 'Z':
        return 7;
      case 'G':
      case 'Q':
        return 8;
      case 'H':
      case 'R':
        return 9;
      default:
        throw new Error('Illegal character: ' + c);
    }
  }

  private calculate(s: string): number {
    s = s.toUpperCase();
    const chars = s.split('');
    let total = 0;
    for (let i = 0; i < chars.length; i++) {
      const value = this.convert(chars[i]);
      if (i % 2 === 0) {
        total += value;
      } else {
        total += 2 * value;
      }
    }
    let checkDigit = 10 - (total % 10);
    if (checkDigit === 10) {
      checkDigit = 0;
    }
    return checkDigit;
  }

  trackingNumberValidator(controlName: string): ValidatorFn {
    return (group: AbstractControl) => {
      const trackingNumberControl = group.get(controlName);
      const value = trackingNumberControl?.value;

      if (!value) {
        group.setErrors(null);
        return null;
      }

      const trackingNumbers = value
        .split(',')
        .map((num: string) => num.trim())
        .filter((num: string) => num.length > 0);

      let hasError = false;

      for (const num of trackingNumbers) {
        const result = this.setTrackingNumberErrors(num, trackingNumberControl);
        if (result) {
          hasError = true;
          break;
        }
      }
      if (hasError) {
        group.setErrors({ invalidTrackingNo: true });
      } else {
        group.setErrors(null);
      }
      return group.errors;
    };
  }

  private setTrackingNumberErrors(
    num: string,
    trackingNumberControl: AbstractControl,
  ) {
    let mid: string;

    switch (num.length) {
      case 18:
        if (!this.oneZedPattern.test(num) && !this.threeZedPattern.test(num)) {
          trackingNumberControl?.setErrors({ invalidTrackingNo: true });
          return { invalidTrackingNo: true };
        }
        mid = num.substring(2, 17);
        break;
      case 11:
        if (!this.elevenDigitPattern.test(num)) {
          trackingNumberControl?.setErrors({ invalidTrackingNo: true });
          return { invalidTrackingNo: true };
        }
        if (this.elevenZDigitPattern.test(num)) {
          trackingNumberControl?.setErrors({ invalidTrackingNo: true });
          return { invalidTrackingNo: true };
        }
        mid = num.substring(1, 10);
        break;
      default:
        trackingNumberControl?.setErrors({ invalidTrackingNo: true });
        return { invalidTrackingNo: true };
    }

    const checkDigit = this.calculate(mid);
    if (!num.endsWith(checkDigit.toString())) {
      trackingNumberControl?.setErrors({ invalidTrackingNo: true });
      return { invalidTrackingNo: true };
    }

    return null;
  }

  /**
   * Validator factory that enforces a minimum length on a tax ID control
   * based on the selected tax type in the same group.
   *
   * Logic:
   * - For types 'PFC' → minimum length = 16
   * - For all other types → minimum length = 13
   * - If the tax ID has content but is shorter than the minimum:
   *     - Adds/merges a `minlength` error to the tax control
   *     - Returns `{ taxIdMinLength: true }` at the group level
   * - If length requirement is satisfied:
   *     - Removes only the `minlength` error from the tax control (preserving others)
   *     - Returns `null`
   *
   * Side effects:
   * - Mutates the `taxField` control’s errors via `setErrors`.
   * - Uses `hasError` and merges existing error map to avoid clobbering unrelated errors.
   *
   * @param taxField - Control name for the tax ID (default: `'taxidPfcNumber'`).
   * @param typeField - Control name for the tax type (default: `'taxidPfc'`).
   * @returns A `ValidatorFn` that validates the group and optionally sets/clears
   *          the `minlength` error on the tax control.
   */
  taxIdMinLengthValidator(
    taxField = 'taxidPfcNumber',
    typeField = 'taxidPfc',
  ): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const taxCtrl = group.get(taxField);
      const typeVal = (group.get(typeField)?.value ?? '')
        .toString()
        .toUpperCase();
      if (!taxCtrl) return null;

      const is16 = typeVal === 'PFC';
      const min = is16 ? 16 : 13;
      const val = (taxCtrl.value ?? '').toString();
      if (val.length > 0 && val.length < min) {
        taxCtrl.setErrors({
          ...(taxCtrl.errors ?? {}),
          minlength: { requiredLength: min, actualLength: val.length },
        });
        return { taxIdMinLength: true };
      } else {
        if (taxCtrl.hasError('minlength')) {
          const errs = { ...(taxCtrl.errors ?? {}) };
          delete errs.minlength;
          taxCtrl.setErrors(Object.keys(errs).length ? errs : null);
        }
        return null;
      }
    };
  }

  uniqueTrackingNumbersValidator(controlName: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!(control instanceof FormArray)) return null;

      const byValue = new Map<string, number[]>();

      control.controls.forEach((group, index) => {
        const tnCtrl = group.get(controlName);
        const raw = tnCtrl?.value as string | null | undefined;
        const value = (raw ?? '').trim().toUpperCase();
        if (!value) return;

        const arr = byValue.get(value);
        if (arr) arr.push(index);
        else byValue.set(value, [index]);
      });

      // Clear only previous duplicateTracking errors (keep others)
      control.controls.forEach((group) => {
        const tnCtrl = group.get(controlName);
        if (!tnCtrl) return;
        const errors = tnCtrl.errors ?? {};
        if ('duplicateTracking' in errors) {
          const { duplicateTracking, ...rest } = errors;
          tnCtrl.setErrors(Object.keys(rest).length ? rest : null);
        }
      });

      // For each duplicate set, attach the error only to the new row (index 0)
      let hasDuplicates = false;

      for (const [, idxs] of byValue) {
        if (idxs.length > 1) {
          hasDuplicates = true;

          const dirtyIdxs = idxs.filter(
            (i) => control.at(i).get(controlName)?.dirty,
          );
          const targetIdx =
            dirtyIdxs.length === 1
              ? dirtyIdxs[0]
              : dirtyIdxs.length > 1
                ? Math.min(...dirtyIdxs)
                : Math.min(...idxs);

          const tnCtrl = control.at(targetIdx).get(controlName);
          if (tnCtrl) {
            tnCtrl.setErrors({
              ...(tnCtrl.errors ?? {}),
              duplicateTracking: true,
            });
          }
        }
      }

      // Array-level error if any duplicates exist
      return hasDuplicates ? { duplicateTrackingInArray: true } : null;
    };
  }
}
